import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { ThemedButton } from '@helpcenter/themed-button';
import type { WrappedComponentProps, MessageDescriptor } from 'react-intl';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { LoginAttemptError } from 'rest/login/login';
import { LoginAttemptErrorType } from 'rest/login/login';
import { DisableViewUntilMount } from 'view/common/disable-view-until-mount';
import { FieldBase } from 'view/common/fields/field-base';
import { TextField } from 'view/common/fields/text-field';
import { PasswordField } from 'view/common/password-field';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { blur, focus } from '@atlassian/help-center-common-util/dom';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';
import { history } from '@atlassian/help-center-common-util/history';
import { search } from '@atlassian/help-center-common-util/location';
import messages from './messages';

export interface LoginFormStatelessProps {
    onSubmit: () => void;
    onEmailFieldChange: (updatedEmail: string) => void;
    onEmailFieldFocus: () => void;
    onPasswordChange: (password: string) => void;
    onForgotPasswordLinkClick: () => void;
    onCreatePasswordLinkClick?: () => void;
    isSubmitting: boolean;
    isSendingForgotPasswordEmail: boolean;
    isSendingCreatePasswordEmail: boolean;
    validEmail: boolean;
    isPasswordEmptyOnSubmit: boolean;
    email: string;
    password: string;
    loginError: MessageDescriptor | undefined;
    atlassianAccountCheckPerformed: boolean;
    canResetPassword: boolean;
    contactAdminLink: string;
    loginAnnouncementLocale?: string;
    loginFailure: LoginAttemptError | null | undefined;
    showLoginConsentError: boolean;
    createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

const USER_EMAIL_ID = 'user-email';
const SUBMIT_BUTTON_ID = 'login-button';
const USER_PASSWORD_ID = 'user-password';

export class LoginFormStateless extends React.Component<LoginFormStatelessProps & WrappedComponentProps> {
    onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }
        // We need to do this here in order to work on chrome. Calling this method on password field focus
        // had no effect.
        this.blurEmailFieldAndFocusSubmitButton();
        this.cleanErrorCodeFromParams();
        this.props.onSubmit();
    };

    blurEmailFieldAndFocusSubmitButton = () => {
        /*
         * We need to blur the email field this way because when typing an email clicking the submit button after (for checking atlassian account)
         * does not blur the email field on Chrome and Edge. So after clicking the button it looks like the email and password field have the focus.
         * There is no way to reference the html element on @atlaskit/textfield that's why we use document.getElementById .
         */
        blur(USER_EMAIL_ID);
        /*
         * In order to create an understandable focus state on errors (and it will be the same one when using keyboard or clicking the button)
         * we need to focus the button after calling blur on the email text field.
         */
        focus(SUBMIT_BUTTON_ID);
    };

    cleanErrorCodeFromParams = () => {
        const params = new URLSearchParams(search());
        if (params.has('error_code')) {
            params.delete('error_code');
            history.replace({
                search: params.toString(),
            });
        }
    };

    onEmailFieldChange = (_: string, email: string) => {
        this.props.onEmailFieldChange(email);
    };

    onPasswordChange = (password: string) => {
        this.props.onPasswordChange(password);
    };

    getEmailErrorMessage = () => {
        if (this.props.validEmail) {
            return undefined;
        }
        const { intl } = this.props;
        return intl.formatMessage(messages.invalidEmailError);
    };

    getPasswordErrorMessage = () => {
        const { intl } = this.props;
        if (this.props.isPasswordEmptyOnSubmit) {
            return intl.formatMessage(messages.emptyPasswordError);
        }

        return intl.formatMessage(messages.incorrectPasswordError);
    };

    getPasswordField = () => {
        const {
            atlassianAccountCheckPerformed,
            isSubmitting,
            isSendingForgotPasswordEmail,
            password,
            intl,
            loginFailure,
            isPasswordEmptyOnSubmit,
        } = this.props;

        if (atlassianAccountCheckPerformed) {
            const isInvalid =
                loginFailure?.errorType === LoginAttemptErrorType.INVALID_CREDENTIALS || isPasswordEmptyOnSubmit;
            const passwordLabelAndPlaceholder = intl.formatMessage(messages.passwordLabelAndPlaceholder);

            return (
                <FieldBase label={passwordLabelAndPlaceholder} htmlFor={USER_PASSWORD_ID}>
                    <PasswordField
                        id={USER_PASSWORD_ID}
                        testId={USER_PASSWORD_ID}
                        value={password}
                        onChange={this.onPasswordChange}
                        isDisabled={isSubmitting || isSendingForgotPasswordEmail}
                        errorMessage={this.getPasswordErrorMessage()}
                        isInvalid={isInvalid}
                        hideStrengthMeter
                        autoFocus
                    />
                </FieldBase>
            );
        }
        return undefined;
    };

    getButtonText = () => {
        const { atlassianAccountCheckPerformed, intl } = this.props;
        if (!atlassianAccountCheckPerformed) {
            return intl.formatMessage(messages.nextButton);
        }

        return intl.formatMessage(messages.continueButton);
    };

    getSetPasswordLink = () => {
        const {
            atlassianAccountCheckPerformed,
            canResetPassword,
            onCreatePasswordLinkClick,
            isSubmitting,
            isSendingCreatePasswordEmail,
        } = this.props;
        if (atlassianAccountCheckPerformed && canResetPassword && onCreatePasswordLinkClick) {
            return (
                <ForgotPasswordLinkContainer>
                    <div>
                        <FormattedMessage {...messages.dontHavePasswordText} />
                    </div>
                    <ThemedButton
                        id="set-password-link"
                        testId="set-password-link"
                        appearance="link"
                        onClick={onCreatePasswordLinkClick}
                        actionSubjectId="createPassword"
                        isDisabled={isSubmitting}
                        isLoading={isSendingCreatePasswordEmail}
                    >
                        <FormattedMessage {...messages.createPasswordLink} />
                    </ThemedButton>
                </ForgotPasswordLinkContainer>
            );
        }
        return undefined;
    };

    getForgotPasswordLink = () => {
        const {
            atlassianAccountCheckPerformed,
            canResetPassword,
            onForgotPasswordLinkClick,
            intl,
            isSubmitting,
            isSendingForgotPasswordEmail,
        } = this.props;
        if (atlassianAccountCheckPerformed && canResetPassword) {
            const linkText = intl.formatMessage(messages.resetPasswordLink);
            return (
                <ForgotPasswordLinkContainer>
                    <ThemedButton
                        id="forgot-password-link"
                        testId="forgot-password-link"
                        appearance="link"
                        onClick={onForgotPasswordLinkClick}
                        actionSubjectId="forgotPassword"
                        isDisabled={isSubmitting}
                        isLoading={isSendingForgotPasswordEmail}
                    >
                        {linkText}
                    </ThemedButton>
                </ForgotPasswordLinkContainer>
            );
        }
        return undefined;
    };

    getLoginError = () => {
        return (
            <>
                {this.getErrorFromParams()}
                {this.getLoginErrorOnSubmitting()}
            </>
        );
    };

    getLoginConsentError = () => {
        if (this.props.showLoginConsentError) {
            const { createAnalyticsEvent } = this.props;
            createAnalyticsEvent &&
                createAnalyticsEvent({
                    analyticsType: UI_EVENT_TYPE,
                    action: 'viewed',
                    actionSubjectId: `showConsentError`,
                }).fire();

            return (
                <SectionMessage appearance="warning" testId="login-consent-error">
                    <FormattedMessage {...messages.loginConsentErrorMessage} />
                </SectionMessage>
            );
        }
        return undefined;
    };

    getLoginErrorOnSubmitting = () => {
        const { intl, loginError, loginFailure, contactAdminLink } = this.props;
        const notInvalidCredentials = loginFailure?.errorType !== LoginAttemptErrorType.INVALID_CREDENTIALS;

        if (loginError && notInvalidCredentials) {
            return (
                <div aria-live="assertive" role="alert">
                    <SectionMessage appearance="error" testId="login-error-on-submit-section">
                        <FormattedMessage
                            {...loginError}
                            values={{
                                contactAdminLink: (
                                    <ContactAdminLink dangerouslySetInnerHTML={{ __html: contactAdminLink }} />
                                ),
                                forgotPasswordLink: (
                                    <UseTypographyMigratedComponent
                                        migratedComponent={
                                            <Text weight="semibold">
                                                {intl.formatMessage(messages.resetPasswordLinkInlineBold)}
                                            </Text>
                                        }
                                    >
                                        {/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
                                        <strong>{intl.formatMessage(messages.resetPasswordLinkInlineBold)}</strong>
                                    </UseTypographyMigratedComponent>
                                ),
                            }}
                        />
                    </SectionMessage>
                </div>
            );
        }
        return undefined;
    };

    getErrorFromParams = () => {
        const { intl } = this.props;
        const params = new URLSearchParams(search());
        const errorCode = params.get('error_code');
        if (errorCode) {
            const errorMessage =
                errorCode === 'access_denied'
                    ? intl.formatMessage(messages.accessDeniedErrorCodeMessage)
                    : intl.formatMessage(messages.defaultErrorCodeMessage);
            return <SectionMessage appearance="error">{errorMessage}</SectionMessage>;
        }
        return undefined;
    };

    getEmailField = () => {
        const {
            intl,
            isSendingForgotPasswordEmail,
            isSubmitting,
            atlassianAccountCheckPerformed,
            email,
            onEmailFieldFocus,
        } = this.props;

        return (
            <DisableViewUntilMount name={'login-email-field'}>
                <TextField
                    id={USER_EMAIL_ID}
                    shouldFitContainer
                    label={intl.formatMessage(messages.emailLabel)}
                    onChange={this.onEmailFieldChange}
                    errorMessage={this.getEmailErrorMessage()}
                    ariaDescribedby="field-error"
                    disabled={isSubmitting || isSendingForgotPasswordEmail || atlassianAccountCheckPerformed}
                    defaultValue={email}
                    onFocus={onEmailFieldFocus}
                    autoComplete="email"
                    autoFocus={!this.props.showLoginConsentError}
                />
            </DisableViewUntilMount>
        );
    };

    getSubmitButton = () => {
        const { isSendingForgotPasswordEmail, isSubmitting, atlassianAccountCheckPerformed } = this.props;
        const buttonText = this.getButtonText();
        const buttonActionSubjectId = atlassianAccountCheckPerformed ? 'submitLoginForm' : 'checkLoginEmail';

        return (
            <ThemedButton
                id={SUBMIT_BUTTON_ID}
                testId={SUBMIT_BUTTON_ID}
                type="submit"
                appearance="primary"
                shouldFitContainer
                isLoading={isSubmitting}
                isDisabled={isSendingForgotPasswordEmail}
                actionSubjectId={buttonActionSubjectId}
            >
                {buttonText}
            </ThemedButton>
        );
    };

    getForm() {
        return (
            <Form id="login-form" data-testid="login-form" onSubmit={this.onSubmit}>
                {this.getLoginError()}
                {this.getLoginConsentError()}
                {this.getEmailField()}
                {this.getPasswordField()}
                {this.getSubmitButton()}
            </Form>
        );
    }

    render() {
        const { loginAnnouncementLocale } = this.props;
        return (
            <AnalyticsContext data={loginAnnouncementLocale ? { attributes: { loginAnnouncementLocale } } : {}}>
                {this.getForm()}
                {this.getForgotPasswordLink()}
                {this.getSetPasswordLink()}
                <FireScreenEventOnMount />
            </AnalyticsContext>
        );
    }
}

export default injectIntl(LoginFormStateless);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Form = styled.form`
    #login-button {
        margin-top: ${token('space.300', '24px')};
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ForgotPasswordLinkContainer = styled.div`
    margin-top: ${token('space.300', '24px')};
    text-align: center;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ContactAdminLink = styled.div`
    display: inline;
`;
